import { useMemo } from 'react';
import { cn } from '@rocco/utils/cn';
import { Badge } from '@rocco/components/badge';
import { useUser } from '~/modules/user';
import type { PartialConversation } from '../../types';
import { useChat } from '../../hooks/use-chat';
import {
    getChatParticipants,
    getChatTitle,
    getPreviewMessage,
} from '../../utils';
import { ParticipantAvatar } from '../avatar/ParticipantAvatar';

/**
 * A conversation item in the chat inbox list.
 * @see {@link ChatInboxList}
 */
export function ChatInboxListItem({
    conversation,
}: {
    conversation: PartialConversation;
}) {
    const user = useUser();
    const { openChatBox } = useChat();
    const otherParticipants = useMemo(() => {
        return getChatParticipants(conversation, user.ID);
    }, [conversation, user.ID]);
    const messagePreview = useMemo(() => {
        return getPreviewMessage(conversation.LatestMessage, user.ID);
    }, [user.ID, conversation.LatestMessage]);
    const unreadCount = useMemo(() => {
        return conversation.UnreadMessageCount || Number(conversation.Unread);
    }, [conversation.UnreadMessageCount, conversation.Unread]);

    return (
        <li
            className="flex items-center gap-3 rounded-lg bg-white/40 p-4 text-black hover:bg-white/70"
            role="button"
            aria-label="Open conversation"
            onClick={() => openChatBox(Number(conversation.ID))}
        >
            <ParticipantAvatar
                size="square48"
                participant={otherParticipants[0]}
            />
            <div className="min-w-0 flex-1 space-y-1">
                <div className="truncate text-4 font-medium">
                    {getChatTitle(conversation, otherParticipants)}
                </div>
                <div className="truncate text-3.5">{messagePreview}</div>
            </div>
            <Badge
                size="centeredCompact"
                color="red"
                className={cn(unreadCount <= 0 && 'invisible')}
            >
                {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
        </li>
    );
}
